import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Feature, Point } from 'geojson';
import { OperationalStatusEnum } from 'fleet-data-model';
import assetMapApi from '../api/asset-map-markers-api';
import { AppState } from '~/reducers';
import { selectMapSelectedAssetIds } from '~/domain/inventory-v2/store/inventory-v2-selection-selectors';
import type { AppDispatch } from '~/store/Store';
import { AssetMapMarkerFeatureProps } from '../models/super-cluster-markers';
import { exportFilters } from '~/domain/inventory-list/utils/list-filter-utils';

const useUpdateAssetMapQuery = () => {
  const dispatch = useDispatch<AppDispatch>();
  const filtersValues = useSelector((state: AppState) => state.assetListFilter.filtersValues);
  const selectedAssetIds = useSelector(selectMapSelectedAssetIds);

  const updateAssetMapLocationQuery = useCallback(
    (iprId: string, latitude?: number, longitude?: number) => {
      if (latitude !== undefined && longitude !== undefined) {
        dispatch(
          assetMapApi.util.updateQueryData('getAssetMap', exportFilters(filtersValues), (draft) => {
            const asset = draft?.elements?.features.find(
              (a: Feature<Point, AssetMapMarkerFeatureProps>) => a.properties.iprId === iprId
            );
            if (asset) {
              asset.geometry.coordinates[0] = longitude;
              asset.geometry.coordinates[1] = latitude;
              asset.properties.selected = selectedAssetIds.includes(iprId);
            }
          })
        );
      }
    },
    [dispatch, filtersValues, selectedAssetIds]
  );

  const updateAssetMapStatusQuery = useCallback(
    (iprId: string, status: OperationalStatusEnum | undefined) => {
      dispatch(
        assetMapApi.util.updateQueryData('getAssetMap', exportFilters(filtersValues), (draft) => {
          const asset = draft?.elements.features.find(
            (a: Feature<Point, AssetMapMarkerFeatureProps>) => a.properties.iprId === iprId
          );
          if (asset) {
            asset.properties.operationalStatus = status;
            asset.properties.selected = selectedAssetIds.includes(iprId);
          }
        })
      );
    },
    [dispatch, filtersValues, selectedAssetIds]
  );

  const updateAssetMapSelectedQuery = useCallback(
    (selectedIprIds: string[]) => {
      const selected = new Set(selectedIprIds);
      dispatch(
        assetMapApi.util.updateQueryData('getAssetMap', exportFilters(filtersValues), (draft) => {
          draft?.elements?.features.forEach((asset: Feature<Point, AssetMapMarkerFeatureProps>) => {
            if (asset.properties.iprId) {
              asset.properties.selected = selected.has(asset.properties.iprId);
            }
          });
        })
      );
    },
    [dispatch, filtersValues]
  );

  return { updateAssetMapLocationQuery, updateAssetMapStatusQuery, updateAssetMapSelectedQuery };
};

export default useUpdateAssetMapQuery;
